import { createPortal } from "react-dom";
import { Package } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { cn } from "@/lib/utils";

import type { InboundSourceType } from "./inboundApi";
import { inboundReceiveOrderLabel } from "./inboundArrivalUnitKind";

export type InboundArrivalOrderPickSource = {
  source_type: InboundSourceType;
  source_id: string;
  source_number: string | null;
};

export function InboundReceiveArrivalOrderPickModal({
  open,
  packageNo,
  vendorName,
  sources,
  onSelect,
  onCancel,
}: {
  open: boolean;
  packageNo: number;
  vendorName?: string | null;
  sources: InboundArrivalOrderPickSource[];
  onSelect: (source: InboundArrivalOrderPickSource) => void;
  onCancel: () => void;
}): JSX.Element | null {
  if (!open || sources.length === 0) return null;

  const vendor = vendorName?.trim() ?? "";

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <div
        className="mt-4 w-full max-w-lg rounded-lg border border-slate-200 bg-white p-4 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">Multiple orders on this arrival</h2>
        <p className="mt-1 text-sm text-slate-600">
          ARRIVED carton {String(packageNo)}
          {vendor !== "" ? ` (${vendor})` : ""} is linked to more than one order. Which order should
          open?
        </p>
        <ul className="mt-4 space-y-2">
          {sources.map((source) => {
            const label = inboundReceiveOrderLabel({
              sourceNumber: source.source_number,
              sourceId: source.source_id,
            });
            return (
              <li key={`${source.source_type}:${source.source_id}`}>
                <button
                  type="button"
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-left",
                    "hover:border-sky-300 hover:bg-sky-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500",
                  )}
                  onClick={() => onSelect(source)}
                >
                  <Package className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
                  <span className="text-base font-semibold text-slate-900">{label}</span>
                </button>
              </li>
            );
          })}
        </ul>
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
